import 'range-slider-wc';

import {debounce} from '../utils/debounce';
import {filterSearchParams} from '../utils/search-params';
import initPagination from './pagination';
import {initWishlistCallback} from '../utils/wishlist';

const selectors = {
  resultsNavbar: '[data-results-navbar]',
  resultsGrid: '[data-results-grid]',
  pagination: '[data-results-pagination]',
  filterToggle: '[data-results-filter-toggle]',
  filterDrawer: '[data-results-filter-drawer]',
  filterContent: '[data-results-filter-content]',
  filterClearLink: '[data-results-filter-clear-link]',
  filterPriceRangeSlider: '[data-results-filter-price-range-slider]',
  filterPriceRangeMin: '[data-results-filter-price-range-min]',
  filterPriceRangeMax: '[data-results-filter-price-range-max]',
  filterFooter: '[data-results-filter-footer]',
  filterTopbar: '[data-results-filter-topbar]',
  layoutSwitchButton: '[data-results-layout-switch]',
  sortByToggle: '[data-sort-toggle]',
  sortByModal: '[data-sort-modal]',
  resultsCount: '[data-results-count]',
};

const classes = {
  isHidden: 'is-hidden',
  isVisible: 'is-visible',
  isSelected: 'is-selected',
  layoutSwitchSelected: 'layout-switch__btn--selected',
  filterIsVisible: 'filter-is-visible',
  sortByActive: 'sort__modal--active',
  bodySortActive: 'sort-is-active',
};

// Utility function to deduplicate filter parameters in form data
const deduplicateFormData = (formData) => {
  // Create maps to store unique values by parameter name
  const filterParams = new Map();
  const otherParams = new Map();

  // Organize all form data, grouping filter parameters
  for (const [key, value] of formData.entries()) {
    if (key.includes('filter.')) {
      if (!filterParams.has(key)) {
        filterParams.set(key, new Set());
      }
      filterParams.get(key).add(value);
    } else {
      // For non-filter parameters, just store the latest value
      otherParams.set(key, value);
    }
  }

  // Create a new FormData object with deduplicated values
  const deduplicatedFormData = new FormData();

  // Add all non-filter parameters
  for (const [key, value] of otherParams.entries()) {
    deduplicatedFormData.append(key, value);
  }

  // Add deduplicated filter parameters
  for (const [key, valueSet] of filterParams.entries()) {
    for (const value of valueSet) {
      deduplicatedFormData.append(key, value);
    }
  }

  return deduplicatedFormData;
};

const ResultsFilterSort = (container, id) => {
  const defaultSortBy = container.dataset.defaultSortBy || 'manual';
  const resultsGrid = container.querySelector(selectors.resultsGrid);
  const resultsNavbar = container.querySelector(selectors.resultsNavbar);
  let selectedLayoutView = window.sessionStorage.getItem('resultsLayoutView');

  initNavbar();
  initPagination(id);
  initWishlistCallback(selectors.resultsGrid);

  function initNavbar() {
    if (!resultsNavbar || !resultsGrid) return;

    const filterDrawer = container.querySelector(selectors.filterDrawer);
    const filterContent = container.querySelectorAll(selectors.filterContent);
    const filterFooter = container.querySelector(selectors.filterFooter);
    const filterTopbar = container.querySelector(selectors.filterTopbar);
    const pagination = container.querySelectorAll(selectors.pagination);
    const sortByToggle = container.querySelector(selectors.sortByToggle);
    const sortByModal = container.querySelector(selectors.sortByModal);
    let sortByIsExpanded = false;

    // Add event handlers to navbar filter dropdowns
    initNavbarFilterDropdowns();

    const renderPage = async (searchParams, currentFilter = false) => {
      const url = `${window.location.pathname}?section_id=${id}&${searchParams}`;
      try {
        const response = await fetch(url);
        if (response.ok === false) {
          throw new Error(`Fetch "${id}" section response was not OK`);
        }
        const html = await response.text();
        const newDocument = new DOMParser().parseFromString(html, 'text/html');

        resultsGrid.innerHTML = newDocument.querySelector(selectors.resultsGrid).innerHTML;

        // Update the results count
        const resultsCount = container.querySelector(selectors.resultsCount);
        const newResultsCount = newDocument.querySelector(selectors.resultsCount);
        if (resultsCount && newResultsCount) {
          resultsCount.innerHTML = newResultsCount.innerHTML;
        }

        // Replace each filter content separately
        if (filterContent) {
          filterContent.forEach((content, index) => {
            // Don't reload the current filter because it causes bugs
            if (currentFilter && content === currentFilter) {
              return;
            }
            const filterContentNew = newDocument.querySelectorAll(selectors.filterContent);
            content.innerHTML = filterContentNew[index].innerHTML;
          });
        }

        if (filterFooter) {
          filterFooter.innerHTML = newDocument.querySelector(selectors.filterFooter).innerHTML;
        }

        if (filterTopbar) {
          filterTopbar.innerHTML = newDocument.querySelector(selectors.filterTopbar).innerHTML;
        }

        if (pagination) {
          const paginationNew = newDocument.querySelectorAll(selectors.pagination);
          pagination.forEach((item, index) => {
            item.innerHTML = paginationNew[index] ? paginationNew[index].innerHTML : '';
          });
          initPagination(id);
        }

        history.replaceState(
          {searchParams},
          '',
          `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
        );
        initPriceRangeSlider();

        // Update sizes attr for images if alt layout is selected
        if (selectedLayoutView === 'alt') {
          updateImageSizesAttr();
        }

        // Dispatch event that results grid has been updated with new content
        document.dispatchEvent(new CustomEvent('results-grid:updated'));

        // Wishlist Plus (Swym) - custom event dispatched to initialise button for new products loaded
        document.dispatchEvent(new CustomEvent('swym:products-loaded'));

        // Reinitialize event handlers for navbar filter dropdowns
        initNavbarFilterDropdowns();
      } catch (err) {
        window.console.error(err);
      }
    };

    function updateImageSizesAttr() {
      const resultsGridImages = resultsGrid.querySelectorAll('img');

      resultsGridImages.forEach((image) => {
        image.sizes = resultsGrid.dataset.imageSizesAlt;
      });
    }

    const toggleSortByModal = () => {
      if (!sortByToggle || !sortByModal) return;
      sortByModal.style.height = sortByIsExpanded ? '0' : `${sortByModal.scrollHeight}px`;
      sortByModal.classList.toggle(classes.sortByActive);
      sortByIsExpanded = !sortByIsExpanded;
      sortByToggle.ariaExpanded = sortByIsExpanded;
      document.body.classList.add(classes.bodySortActive);
    };

    const handleNavbarFormSubmit = async (event) => {
      event.preventDefault();

      // Get the form element - whether the event target is a direct form element or nested
      const form = event.target.closest('form') || resultsNavbar;

      // Create the form data and deduplicate parameters
      const formData = new FormData(form);
      const deduplicatedFormData = deduplicateFormData(formData);

      const searchParams = new URLSearchParams(deduplicatedFormData);
      const keyValuePairsToAdd = [];
      const keyValuePairsToRemove = [{key: 'sort_by', value: defaultSortBy}, {key: 'products-per-page'}];
      const filterPriceRangeMin = container.querySelector(selectors.filterPriceRangeMin);
      if (filterPriceRangeMin) {
        keyValuePairsToRemove.push({key: filterPriceRangeMin.name, value: filterPriceRangeMin.min});
      }
      const filterPriceRangeMax = container.querySelector(selectors.filterPriceRangeMax);
      if (filterPriceRangeMax) {
        keyValuePairsToRemove.push({key: filterPriceRangeMax.name, value: filterPriceRangeMax.max});
      }
      if (searchParams.has('filter.v.m.filter.size')) {
        keyValuePairsToAdd.push({key: 'filter.v.availability', value: '1'});
      } else {
        keyValuePairsToRemove.push({key: 'filter.v.availability'});
      }
      const filteredSearchParams = filterSearchParams(searchParams, keyValuePairsToAdd, keyValuePairsToRemove);
      const currentFilter = event.target.closest(selectors.filterContent);
      if (event.target.name === 'products-per-page') {
        const rootUrl = window.theme.locale && !window.theme.locale.primary ? window.theme.locale.rootUrl : '';
        const url = `${rootUrl}/cart/update.js`;
        const options = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({attributes: {collection_products_per_page: event.target.value}}),
        };
        try {
          await fetch(url, options);
        } catch (err) {
          window.console.error(err);
        }
      }
      renderPage(filteredSearchParams, currentFilter);

      // Close sort by modal if open
      if (sortByIsExpanded) {
        toggleSortByModal();
      }
    };

    const debouncedNavbarFormSubmit = debounce((event) => {
      handleNavbarFormSubmit(event);
    }, 500);

    resultsNavbar.addEventListener('input', debouncedNavbarFormSubmit);
    resultsNavbar.addEventListener('submit', handleNavbarFormSubmit);

    // Filters
    if (filterDrawer) {
      // Toggle filters drawer
      const handleFilterToggles = (event) => {
        const filterToggle = event.target.closest(selectors.filterToggle);
        if (!filterToggle) return;

        if (filterDrawer.classList.contains(classes.isHidden)) {
          filterDrawer.classList.remove(classes.isHidden);
          filterDrawer.classList.add(classes.isVisible);
          filterDrawer.setAttribute('aria-hidden', false);
          document.body.classList.add(classes.filterIsVisible);
        } else {
          filterDrawer.classList.add(classes.isHidden);
          filterDrawer.classList.remove(classes.isVisible);
          filterDrawer.setAttribute('aria-hidden', true);
          document.body.classList.remove(classes.filterIsVisible);
        }
      };

      // Use document to catch all clicks on filter toggle buttons
      document.addEventListener('click', function (event) {
        const filterToggle = event.target.closest(selectors.filterToggle);
        if (filterToggle) {
          handleFilterToggles(event);
        }
      });

      // Clear filters links
      const handleClearFiltersLinkClick = (event) => {
        const filterClearLink = event.target.closest(selectors.filterClearLink);
        if (!filterClearLink) return;

        event.preventDefault();
        const searchParams = filterClearLink.href.split('?')[1] ?? '';
        renderPage(searchParams);
      };
      filterDrawer.addEventListener('click', handleClearFiltersLinkClick);
    }

    if (filterContent) {
      // Price range filter
      initPriceRangeSlider();
    }

    // Layout switcher
    const layoutSwitchButtons = container.querySelectorAll(selectors.layoutSwitchButton);
    let imgSizesUpdated = false;

    const handleLayoutSwitch = (target) => {
      const layout = target.dataset.resultsLayoutSwitch;
      const classesToRemove = Array.from(resultsGrid.classList).filter((value) => value.includes('results-grid--'));
      resultsGrid.classList.add(`results-grid--${layout}`);
      resultsGrid.classList.remove(...classesToRemove);

      // Update sizes attr for images if alt layout is selected and sizes have not been updated already
      if (layout === 'alt' && !imgSizesUpdated) {
        updateImageSizesAttr();
        imgSizesUpdated = true;
      }

      layoutSwitchButtons.forEach((button) => {
        button.setAttribute('aria-pressed', 'false');
        button.removeAttribute('disabled');
        button.classList.remove(classes.layoutSwitchSelected);
      });
      target.setAttribute('disabled', 'disabled');
      target.setAttribute('aria-pressed', 'true');
      target.classList.add(classes.layoutSwitchSelected);
      selectedLayoutView = layout;
      window.sessionStorage.setItem('resultsLayoutView', layout);
    };

    if (selectedLayoutView === 'alt') {
      const layoutSwitchAltButton = [...layoutSwitchButtons].find(
        (button) => button.dataset.resultsLayoutSwitch === 'alt'
      );
      if (layoutSwitchAltButton) {
        handleLayoutSwitch(layoutSwitchAltButton);
      }
    }

    layoutSwitchButtons.forEach((layoutSwitchButton) => {
      layoutSwitchButton.addEventListener('click', (event) => handleLayoutSwitch(event.target));
    });

    // Sort by modal toggle
    sortByToggle?.addEventListener('click', (event) => {
      event.preventDefault();
      toggleSortByModal();
    });
  }

  function initPriceRangeSlider() {
    const filterPriceRangeSlider = container.querySelector(selectors.filterPriceRangeSlider);
    const filterPriceRangeMin = container.querySelector(selectors.filterPriceRangeMin);
    const filterPriceRangeMax = container.querySelector(selectors.filterPriceRangeMax);
    if (!filterPriceRangeSlider || !filterPriceRangeMin || !filterPriceRangeMax) return;

    filterPriceRangeSlider.addEventListener('input', (event) => {
      const {valueMin, valueMax} = event.detail;
      filterPriceRangeMin.value = valueMin;
      filterPriceRangeMax.value = valueMax;
    });

    filterPriceRangeSlider.addEventListener('change', () => {
      if (resultsNavbar.requestSubmit) {
        return resultsNavbar.requestSubmit();
      }

      const submitButton = resultsNavbar.querySelector('[type="submit"]');
      if (!submitButton) return;

      submitButton.click();
    });

    filterPriceRangeMin.addEventListener(
      'input',
      debounce(() => {
        filterPriceRangeSlider.valueMin = filterPriceRangeMin.value;
      }, 500)
    );

    filterPriceRangeMax.addEventListener(
      'input',
      debounce(() => {
        filterPriceRangeSlider.valueMax = filterPriceRangeMax.value;
      }, 500)
    );
  }

  // Initialize event handlers for navbar filter dropdowns
  function initNavbarFilterDropdowns() {
    const navbarFilterDropdowns = container.querySelectorAll('.results-navbar__filter-dropdown');

    navbarFilterDropdowns.forEach((dropdown) => {
      const checkboxes = dropdown.querySelectorAll('input[type="checkbox"]');

      checkboxes.forEach((checkbox) => {
        // First remove any existing event listeners to prevent duplicates
        checkbox.removeEventListener('change', handleNavbarCheckboxChange);

        // Then add the new event listener
        checkbox.addEventListener('change', handleNavbarCheckboxChange);
      });
    });
  }

  // Handle checkbox changes in navbar filter dropdowns
  function handleNavbarCheckboxChange(event) {
    const navbarCheckbox = event.target;

    // Find the equivalent checkbox in the drawer filters
    const filterName = navbarCheckbox.name;
    const filterValue = navbarCheckbox.value;

    // Find the matching drawer checkbox with the same name and value
    const drawerCheckbox = container.querySelector(
      `${selectors.filterDrawer} input[name="${filterName}"][value="${filterValue}"]`
    );

    if (drawerCheckbox) {
      // Update the drawer checkbox to match the navbar checkbox
      drawerCheckbox.checked = navbarCheckbox.checked;

      // Trigger a change event on the drawer checkbox
      const changeEvent = new Event('change', {bubbles: true});
      drawerCheckbox.dispatchEvent(changeEvent);

      // Prevent default to avoid running the navbar submission logic
      event.preventDefault();
    } else {
      console.warn(`Could not find matching drawer checkbox for ${filterName}=${filterValue}`);

      // If no drawer checkbox found, fall back to form submission
      const form = resultsNavbar;
      const formData = new FormData(form);
      const deduplicatedFormData = deduplicateFormData(formData);

      const searchParams = new URLSearchParams(deduplicatedFormData);
      const keyValuePairsToAdd = [];
      const keyValuePairsToRemove = [{key: 'sort_by', value: defaultSortBy}, {key: 'products-per-page'}];

      const filterPriceRangeMin = container.querySelector(selectors.filterPriceRangeMin);
      if (filterPriceRangeMin) {
        keyValuePairsToRemove.push({key: filterPriceRangeMin.name, value: filterPriceRangeMin.min});
      }

      const filterPriceRangeMax = container.querySelector(selectors.filterPriceRangeMax);
      if (filterPriceRangeMax) {
        keyValuePairsToRemove.push({key: filterPriceRangeMax.name, value: filterPriceRangeMax.max});
      }

      if (searchParams.has('filter.v.m.filter.size')) {
        keyValuePairsToAdd.push({key: 'filter.v.availability', value: '1'});
      } else {
        keyValuePairsToRemove.push({key: 'filter.v.availability'});
      }

      const filteredSearchParams = filterSearchParams(searchParams, keyValuePairsToAdd, keyValuePairsToRemove);
      renderPage(filteredSearchParams, null);
    }
  }
};

export default ResultsFilterSort;
